import { debugLog } from "@/helpers/debug-log-helper.js";
import { endorsementTypes } from "@/constants/endorsements";
import { hasEndorsementType } from "@/helpers/endorsements-helper";

export function isUsableVin(vin) {
    return normalizeVehicleToken(vin).length >= 11;
}

export function policyVehicleHasEndorsements(policyVehicle) {
    const endorsements = policyVehicle?.endorsements;
    return (
        hasEndorsementType(endorsements, endorsementTypes.EDUCATOR) ||
        hasEndorsementType(endorsements, endorsementTypes.PARKING_GUARD)
    );
}

export function getPolicyVehicleDisplayLabel(policyVehicle) {
    return formatVehicleYmmsLabel(
        policyVehicle?.vehicleYear,
        policyVehicle?.vehicleMake,
        policyVehicle?.vehicleModel
    );
}

export function getEnteredVehicleDisplayLabel(vehicle) {
    return formatVehicleYmmsLabel(vehicle?.year, vehicle?.make, vehicle?.model);
}

export function formatMaskedVin(vin) {
    const normalized = normalizeVehicleToken(vin);
    if (normalized.length < 11) {
        return "";
    }

    return `VIN: <span class="policy-vehicle-vin-mask">••••••</span>${escapeHtml(
        normalized.slice(-6)
    )}`;
}

export function formatVehicleYmmsLabel(year, make, model) {
    return [year, make, model].filter(Boolean).join(" ").toUpperCase();
}

export function escapeHtml(value) {
    return (value ?? "")
        .toString()
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

export function doesPolicyVehicleMatchEnteredVehicle(policyVehicle, enteredVehicle) {
    if (!policyVehicle || !enteredVehicle) {
        return false;
    }

    const policyVin = normalizeVehicleToken(policyVehicle.vin);
    const enteredVin = normalizeVehicleToken(enteredVehicle.vin);
    debugLog("policyVin", policyVin);
    debugLog("enteredVin", enteredVin);
    if (isUsableVin(policyVehicle.vin) && isUsableVin(enteredVehicle.vin)) {
        debugLog("vin compare", policyVin === enteredVin);
        return policyVin === enteredVin;
    }

    const yearMatch = yearsMatch(policyVehicle.vehicleYear, enteredVehicle.year);
    const makeMatch = tokensMatch(policyVehicle.vehicleMake, enteredVehicle.make);
    const modelMatch = tokensMatch(policyVehicle.vehicleModel, enteredVehicle.model);
    debugLog(
        "year compare",
        yearMatch + " " + policyVehicle.vehicleYear + "|" + enteredVehicle.year
    );
    debugLog(
        "make compare",
        makeMatch + " " + policyVehicle.vehicleMake + "|" + enteredVehicle.make
    );
    debugLog(
        "model compare",
        modelMatch + " " + policyVehicle.vehicleModel + "|" + enteredVehicle.model
    );
    return yearMatch && makeMatch && modelMatch;
}

export function findMatchingPolicyVehicle(policyVehicles, enteredVehicle) {
    if (!Array.isArray(policyVehicles) || !enteredVehicle) {
        return null;
    }

    return (
        policyVehicles.find((policyVehicle) =>
            doesPolicyVehicleMatchEnteredVehicle(policyVehicle, enteredVehicle)
        ) ?? null
    );
}

export function getSelectedPolicyVehicle(policyVehicles, policyVehicleId) {
    if (!Array.isArray(policyVehicles) || policyVehicleId == null) {
        return null;
    }

    return (
        policyVehicles.find(
            (policyVehicle) => policyVehicle?.id?.toString() === policyVehicleId?.toString()
        ) ?? null
    );
}

function normalizeVehicleToken(value) {
    return (value ?? "")
        .toString()
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
}

// Years must match exactly. tokensMatch is intentionally fuzzy (for make/model), and would
// treat any two years in the same decade as a match (e.g. 2010 and 2019).
function yearsMatch(left, right) {
    const normalizedLeft = normalizeVehicleToken(left);
    const normalizedRight = normalizeVehicleToken(right);

    if (!normalizedLeft || !normalizedRight) {
        return false;
    }

    return normalizedLeft === normalizedRight;
}

function tokensMatch(left, right) {
    const normalizedLeft = normalizeVehicleToken(left);
    const normalizedRight = normalizeVehicleToken(right);
    if (!normalizedLeft || !normalizedRight) {
        return false;
    }

    if (normalizedLeft === normalizedRight) {
        return true;
    }

    return (
        normalizedLeft.startsWith(normalizedRight) ||
        normalizedRight.startsWith(normalizedLeft) ||
        normalizedLeft.slice(0, 3) === normalizedRight.slice(0, 3)
    );
}
